import type { PaginatedResponse, InsurerType } from "types/DataTypes.ts";
import { api } from "./api.ts";

type GetInsurersArgs = {
  searchTerm?: string;
  limit?: number;
  offset?: number;
};

type GetInsurerByIdArgs = {
  id: string | number;
};

type PatchInsurerArgs = {
  id: string | number;
  data: Partial<InsurerType>;
};

type CreateInsurerArgs = {
  data: Partial<InsurerType>;
};

export const insurerAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getInsurers: build.query<PaginatedResponse<InsurerType>, GetInsurersArgs>({
      query: ({ searchTerm, limit, offset }) => {
        const params = new URLSearchParams();
        // DRF SearchFilter reads the term from `filter` (SEARCH_PARAM in
        // settings), not the default `search`; matches employer-api
        if (searchTerm) params.append("filter", searchTerm);
        if (limit !== undefined) params.append("limit", limit.toString());
        if (offset !== undefined) params.append("offset", offset.toString());

        return {
          url: `api/v1/insurers/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["Insurer"],
    }),
    getInsurerById: build.query<InsurerType, GetInsurerByIdArgs>({
      query: ({ id }) => ({
        url: `api/v1/insurers/${id}/`,
        method: "GET",
      }),
      providesTags: ["Insurer"],
    }),
    patchInsurerById: build.mutation<InsurerType, PatchInsurerArgs>({
      query: ({ id, data }) => ({
        url: `api/v1/insurers/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Insurer", "Files"],
    }),
    createInsurer: build.mutation<InsurerType, CreateInsurerArgs>({
      query: ({ data }) => ({
        url: `api/v1/insurers/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Insurer"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetInsurersQuery,
  useGetInsurerByIdQuery,
  usePatchInsurerByIdMutation,
  useCreateInsurerMutation,
} = insurerAPI;
